import { useSuspenseQuery } from "@apollo/client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  ContextLine,
  DateUI,
  entryFromEventFragment,
  PrimaryFullScreenSpinner,
  TimeLogPersonActor,
  TimeLogSystemActor,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@repo/ui";
import { NotFoundError, neverFunc } from "@repo/utils";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Suspense } from "react";
import { DetailRow, DetailSubRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/events_/$eventId")({
  head: () => opsHead("Event"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { eventId } = useParams({ strict: false });

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminEventDetail($id: ID!) {
        node(id: $id) {
          ... on Event {
            id
            kind
            description
            date
            shortRef
            actor {
              id
            }
            object {
              ... on Node {
                __typename
                id
              }
              ... on UnknownEventObject {
                objectKind
              }
            }
            organisation {
              id
              name
            }
            data
            ...TimeLogEventEntry
          }
        }
      }
    `),
    {
      variables: {
        id: eventId ?? "",
      },
    },
  );

  const eventDetail = data.node;
  if (!eventDetail || eventDetail.__typename !== "Event") {
    throw new NotFoundError();
  }

  const { by } = entryFromEventFragment(eventDetail);
  let actorBadge: ReactNode = null;
  if (by) {
    switch (by.kind) {
      case "PERSON":
        actorBadge = <TimeLogPersonActor actor={by} />;
        break;
      case "SYSTEM":
        actorBadge = <TimeLogSystemActor actor={by} />;
        break;
      default:
        neverFunc(by);
    }
  }

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title="Event"
        contextLine={
          <ContextLine
            current={`#${eventDetail.shortRef}`}
            ancestors={[{ label: "Event", path: "/events" }]}
          />
        }
      />
      <div className="flex flex-row gap-12">
        <div className="w-1/2 flex-none">
          <DetailRow
            label="ID"
            value={<span className="font-mono">{eventDetail.id}</span>}
          />
          <DetailRow label="Kind" value={eventDetail.kind} />
          <DetailRow
            label="Description"
            value={eventDetail.description}
          ></DetailRow>
          <DetailRow
            label="Created At"
            value={
              <Tooltip>
                <TooltipTrigger className="text-left">
                  <DateUI date={eventDetail.date} variant="DATETIME_TZ" />
                </TooltipTrigger>
                <TooltipContent className="bg-white font-mono text-primary">
                  {eventDetail.date.toISOString()}
                </TooltipContent>
              </Tooltip>
            }
          />
          <DetailRow label="Actor">
            {eventDetail.actor ? (
              <>
                {actorBadge && <DetailSubRow label="By" value={actorBadge} />}
                <DetailSubRow
                  label="Kind"
                  value={eventDetail.actor.__typename}
                />
                <DetailSubRow
                  label="ID"
                  value={
                    <span className="font-mono">{eventDetail.actor.id}</span>
                  }
                />
              </>
            ) : (
              "No actor recorded."
            )}
          </DetailRow>
          <DetailRow label="Object">
            {eventDetail.object ? (
              <>
                <DetailSubRow
                  label="Kind"
                  value={
                    eventDetail.object.__typename === "UnknownEventObject"
                      ? eventDetail.object.objectKind
                      : eventDetail.object.__typename
                  }
                />
                <DetailSubRow
                  label="ID"
                  value={
                    <span className="font-mono">{eventDetail.object.id}</span>
                  }
                  copyable
                />
              </>
            ) : (
              "No object recorded."
            )}
          </DetailRow>
          <DetailRow
            label="Organisation"
            value={
              eventDetail.organisation ? (
                <Link to={`/organisations/${eventDetail.organisation.id}`}>
                  {eventDetail.organisation.name}
                </Link>
              ) : (
                "No organisation recorded."
              )
            }
          />
        </div>
        <div className="w-1/2">
          <DetailRow label="Data" className="mb-2" />
          {eventDetail.data ? (
            <pre className="flex-1 overflow-x-auto bg-secondary p-2">
              {JSON.stringify(eventDetail.data, null, 2)}
            </pre>
          ) : (
            "No data recorded."
          )}
        </div>
      </div>
      <Accordion type="single" collapsible className="mt-10">
        <AccordionItem value="item-1">
          <AccordionTrigger hideChevron={false}>Event JSON</AccordionTrigger>
          <AccordionContent>
            <pre className="overflow-auto bg-secondary p-2">
              {JSON.stringify(eventDetail, null, 2)}
            </pre>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </AdminPageContainer>
  );
}
